import React, { useState, useEffect } from "react";

import { useTranslation } from "react-i18next";

import { Player } from "@lottiefiles/react-lottie-player";

import "./index.css";

export default function AnimationContainer(props) {
  const {
    t,
    // i18n
  } = useTranslation("translation");
  const animationData = props.previewAnimation || props.animationData; // Fallback animation file

  const player = React.createRef();

  const totalFrames = animationData.op;
  const frameRate = animationData.fr;
  const [speed, setSpeed] = useState(0.111);
  const [heightCalc, setHeightCalc] = useState("300vh");

  const [prevFrame, setPrevFrame] = useState(0); // Set reference to prevent unnecessary runs

  useEffect(() => {
    // Update frame with forced navigation
    if (props.forcedProgress !== null) {
      const frame = (props.forcedProgress / 100) * totalFrames;
      player.current.setSeeker(frame + props.adjustFrame, true); // go to frame (adjust frame is to account fot matching start/end times when navigating with an offset of 1 frame or optional)

      if (props.forceScrollUpdate) {
        correctScroll(frame);
      }
    }
  }, [props.forcedProgress]);

  useEffect(() => {
    if (speed !== 0.111) {
      const val = (totalFrames * frameRate) / speed;
      setHeightCalc(`calc(300vh + ${1 + val}px)`);
    }
  }, [speed]);

  window.onscroll = function () {
    updateScroll();
  };

  const loadError = () => {
    console.log("Animation error!");
  };

  const isLoaded = () => {
    setSpeed(1);
    props.setTotalTime(totalFrames / frameRate);
    props.setAnimationReady();
    window.scrollTo(0, 0);
  };

  const newFrame = () => {
    const currentFrame = player.current.state.instance.currentFrame;
    if (
      Math.round(currentFrame * 10) / 10 !==
      Math.round(prevFrame * 10) / 10
    ) {
      setPrevFrame(currentFrame);
      if (props.forcedProgress !== null) {
        props.forceAnimationTo(null);
      } // reset after navigation
      props.updatePercentage((currentFrame / totalFrames) * 100);
    }
  };

  const handleEnd = () => {
    props.updatePercentage(100);
  };

  const updateScroll = () => {
    // Update animation position with scroll if active
    if (props.scrollActive) {
      const elem = document.getElementById("animation-player");
      let scrollOffset =
        (elem.offsetTop - window.innerHeight) / (frameRate / speed);
      if (scrollOffset < 1) {
        scrollOffset = 0;
      }
      if (scrollOffset > totalFrames) {
        scrollOffset = totalFrames;
      }

      player.current.setSeeker(scrollOffset, true);
    }
  };

  const correctScroll = (frame) => {
    // Update scroll position after navigation
    window.scrollTo(
      0,
      frame * (frameRate / speed) + (frameRate + 1) + window.innerHeight
    );
    props.forceScroll("reset");
  };

  const getTime = () => {
    // Conversion for usersnap element
    const percent = (prevFrame / totalFrames) * 100;
    const time = (percent / 100) * (totalFrames / frameRate);
    const minutes = Math.floor(time / 60);
    const seconds = (time - minutes * 60).toFixed(2);

    return `${minutes > 9 ? minutes : minutes > 0 ? `0${minutes}` : "00"}:${seconds > 9 ? seconds : seconds > 0 ? `0${seconds}` : "00.00"}`;
  };

  return (
    <div
      id="animation-container"
      className="animation-container"
      style={{
        width: props.bp !== "lg" && props.bp !== "xl" ? "100%" : null,
      }}
    >
      {props.debug && props.previewAnimation ? (
        <div
          role="button"
          style={{
            position: "fixed",
            top: "36px",
            right: "0px",
            backgroundColor: "rgb(243, 86, 91)",
            borderRadius: "2px",
            padding: "6px",
            fontWeight: "bold",
            cursor: "pointer",
            zIndex: "99",
            minWidth: "50px",
            textAlign: "center",
            pointerEvents: "auto",
            userSelect: "none",
          }}
          onClick={() => {
            if (speed === 1) {
              setSpeed(1.5);
            } else if (speed === 1.5) {
              setSpeed(2);
            } else if (speed === 2) {
              setSpeed(4);
            } else if (speed === 4) {
              setSpeed(0.1);
            } else if (speed === 0.1) {
              setSpeed(0.25);
            } else if (speed === 0.25) {
              setSpeed(0.5);
            } else {
              setSpeed(1);
            }
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              if (speed === 1) {
                setSpeed(1.5);
              } else if (speed === 1.5) {
                setSpeed(2);
              } else if (speed === 2) {
                setSpeed(4);
              } else if (speed === 4) {
                setSpeed(0.1);
              } else if (speed === 0.1) {
                setSpeed(0.25);
              } else if (speed === 0.25) {
                setSpeed(0.5);
              } else {
                setSpeed(1);
              }
            }
          }}
          tabIndex={0}
          aria-label={`Current speed: ${speed}`}
        >
          Speed: {speed}
        </div>
      ) : null}
      {props.debug && props.previewAnimation ? (
        <div
          style={{
            position: "fixed",
            top: "0px",
            right: "0px",
            backgroundColor: "rgb(243, 86, 91)",
            borderRadius: "2px",
            padding: "6px",
            fontWeight: "bold",
            zIndex: "99",
            minWidth: "50px",
            textAlign: "center",
            pointerEvents: "auto",
            cursor: "text",
          }}
        >
          Frame: {Math.round(prevFrame * 100) / 100} | Time: {getTime()}
        </div>
      ) : null}

      <div
        className="animation-scroll"
        id="animation-scroll"
        style={{ height: heightCalc }} // This height being set too large or changing after the animation data is loaded cause Axe testing tool to crash. When testing, manually set a value and fix before pushing the updates.
      >
        <div
          id="animation-player"
          className={`animation-player ${
            props.bp !== "lg" && props.bp !== "xl"
              ? ""
              : "animation-player-desktop"
          }`}
          aria-hidden={
            props.scrollActive === false
              ? true
              : props.ariaHideSplashScreens === "animation"
                ? false
                : true
          }
          aria-label={
            Number.isInteger(parseInt(props.currentDescriptionID) - 1) &&
            props.sceneData.Scrolly.descriptions[
              parseInt(props.currentDescriptionID) - 1
            ].props.altTxt !== ""
              ? t(
                  `${
                    props.sceneData.Scrolly.descriptions[
                      parseInt(props.currentDescriptionID) - 1
                    ].props.altTxt
                  }.string`
                )
              : t("contentArea.string")
          }
          style={{
            height: "auto",
          }}
        >
          <div aria-hidden="true" style={{ pointerEvents: "none" }}>
            <Player
              // Main animation component
              ref={player}
              autoplay={false}
              loop={false}
              speed={speed}
              preserveAspectRatio="xMaxYMid meet"
              onEvent={(event) => {
                if (event === "error") loadError();
                if (event === "load") isLoaded();
                if (event === "frame") newFrame();
                if (event === "complete") handleEnd();
              }}
              keepLastFrame // Hold on last frame instead of returning to 0
              src={animationData}
              style={{
                height:
                  props.bp === "lg" || props.bp === "xl"
                    ? "100vh"
                    : "calc(100vh - 200px)",
              }}
            ></Player>
          </div>
        </div>
      </div>
    </div>
  );
}
